import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { cloneDeep, isEmpty, isEqual } from "lodash";
import { useDidUpdate } from "hooks";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import HeaderActionButton from "components/common/header-action-button";
import ActionDialog from "components/complex/dialogs/action";
import TableViewDialog from "components/complex/table-view/table-view-dialog";
import { Box, Divider, MenuItem, TextField } from "@mui/material";
import {
  callDeleteCustomTableView,
  callEditCustomTableViewpromise,
  callSelectCustomTableView,
  CUSTOM_TABLE_VIEW_DELETE_CALL
} from "store/table-view/sagas-actions";
import { errorSelectorById } from "store/error/selector";
import { customTableViewDataSelector } from "store/table-view/selectors";
import { onKeepTableView } from "store/table-view/slice";
import Import from "components/complex/import";
import Export from "components/complex/export";
import useDeviceType from "../common/useDeviceType";
import { callEditUiSettingsUser } from "../../store/users/sagas-actions";
import { listViewTypeOptions, listViewTypes } from "../../constant/data/list-view-types";
import { useLocation } from "react-router-dom";
import { hasTableStateInSearch } from "utils/helpers/tableUrlSync";
import { userDataSelector } from "../../store/user/selectors";

function hasActiveFilters(filterValue) {
  return (filterValue || []).some(
    (f) =>
      f.name !== "tenant_user_id" &&
      f.value !== "" &&
      f.value !== null &&
      f.value !== undefined
  );
}

// List pages pass view column defs (`name` / `header`). Older callers pass raw
// API columns (`column_name`). Export options need the display string either way.
function getColumnLabel(column) {
  if (typeof column === "string" || typeof column === "number") return String(column);
  if (!column || typeof column !== "object") return "";
  const label = column.name ?? column.column_name ?? column.header;
  return label == null || label === "" ? "" : String(label);
}

function useTableViewHeader(
  moduleValue,
  defaultViewText,
  myItemsText,
  permissions,
  tableRef,
  selected,
  tenantUserId,
  viewId,
  viewTypeId,
  action,
  defaultColumns,
  disableMyView = false,
  paginationData,
  isLoading,
  mergePaginationFromUrl,
  syncPaginationToUrl
) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const {
    list,
    isLoading: isLoadingView,
    isFilteredByTenantUser
  } = useSelector((store) => customTableViewDataSelector(store, moduleValue), isEqual);
  const { user } = useSelector(userDataSelector, isEqual);

  useSelector((store) => errorSelectorById(store, CUSTOM_TABLE_VIEW_DELETE_CALL), isEqual);

  const [selectedView, setSelectedView] = useState({});
  // Live StackTable draft (columns/widths/order/groups). Always updated — including on All/My —
  // so Create View can persist current layout without writing into selectedView (which caused loops).
  const [liveTableDraft, setLiveTableDraft] = useState(null);
  const [selectedOriginalView, setSelectedOriginalView] = useState(null);
  const [addView, setAddView] = useState();
  const [viewToEdit, setViewToEdit] = useState();
  const [viewToDeleteId, setViewToDeleteId] = useState();
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [openExportDialog, setOpenExportDialog] = useState(undefined);

  const { search } = useLocation();
  const [isFiltering, setIsFiltering] = useState(hasTableStateInSearch(search));
  const [isGrouping, setIsGrouping] = useState(true);
  // URL merge is only for the initial select (page load / return from detail).
  // User-driven view switches must use the selected view's own filters, otherwise
  // the previous view's URL filters get re-applied on top of the new view.
  const isInitialSelectRef = useRef(true);

  const { isMobile } = useDeviceType();
  const isCardView = listViewTypes[viewTypeId] === "card";

  const setTableViewDraft = useCallback((tV) => {
    setLiveTableDraft(tV);
    setSelectedView((prevState) => {
      if (!prevState?.table_view_id) {
        return prevState;
      }
      if (isEqual(prevState.data, tV)) {
        return prevState;
      }
      return {
        ...prevState,
        data: tV
      };
    });
  }, []);

  function selectView(viewId, viewOverride, withoutLoading = false) {
    setSelectedOriginalView(viewId);
    const listSettings = {};
    listSettings.list_settings = {
      [`${moduleValue}_list_view_id`]: viewId === "default_view" || viewId === "my_view" ? null : viewId
    };

    const view = list.find((v) => String(v.table_view_id) === String(viewId)) || viewOverride;
    const parsedView = view
      ? {
          ...view,
          data: typeof view.data === "string" ? JSON.parse(view.data) : view.data
        }
      : view;

    setSelectedView(parsedView || {});
    // Reset draft to the selected view's saved layout (or null for All/My) until StackTable pushes live state.
    setLiveTableDraft(parsedView?.data || null);
    dispatch(callSelectCustomTableView(tenantUserId, listSettings));
    const isMyView = viewId === "my_view";
    const isCard = listViewTypes[viewTypeId] === "card";

    dispatch(
      onKeepTableView({
        key: moduleValue,
        view: isMyView ? {} : parsedView || {},
        isFilteredByTenantUser: isMyView
      })
    );

    const paginationInitialData = {
      limit: isCard ? 40 : 10,
      skip: 0,
      sortInfo: {},
      groupBy: [],
      filterValue: isMyView
        ? [
            {
              name: "tenant_user_id",
              operator: "eq",
              value: tenantUserId
            }
          ]
        : []
    };
    const newAction = cloneDeep(action);
    let newPaginationData = parsedView?.data
      ? { ...paginationInitialData, ...parsedView.data }
      : paginationInitialData;
    // Guard against legacy sortInfo: true (non-object) which causes SQL "order by undefined" errors
    if (typeof newPaginationData.sortInfo !== "object" || newPaginationData.sortInfo === null) {
      newPaginationData.sortInfo = {};
    }
    if (isInitialSelectRef.current && mergePaginationFromUrl) {
      newPaginationData = mergePaginationFromUrl(newPaginationData);
    }
    isInitialSelectRef.current = false;

    // Drive the filter bar from the view being selected (not the previous URL).
    setIsFiltering(
      Boolean(newPaginationData?.filteringStatus) || hasActiveFilters(newPaginationData?.filterValue)
    );

    newAction.payload.paginationData = { ...newPaginationData, uiViewTypeId: isMobile ? 1 : viewTypeId };
    if (syncPaginationToUrl) {
      syncPaginationToUrl(newAction.payload.paginationData);
    }
    if (withoutLoading || hasTableStateInSearch(search)) {
      newAction.payload.withoutLoading = true;
    }
    if (!isLoading || hasTableStateInSearch(search)) {
      dispatch(newAction);
    }
  }

  let visibleColumnNames = [];
  if (selectedView?.data?.columns) {
    visibleColumnNames = selectedView.data.columns.map(getColumnLabel).filter(Boolean);
  } else if (defaultColumns?.length) {
    visibleColumnNames = defaultColumns.map(getColumnLabel).filter(Boolean);
  }
  const importLabels = {
    contact: "Contacts",
    company: "Companies",
    product_service: "Products/Services",
    opportunity: "Opportunities"
  };

  const onSelectListViewType = (id) => {
    const newUiData = {
      [`${moduleValue}_ui_view_type_id`]: id
    };

    const isCard = listViewTypes[id] === "card";
    const newAction = cloneDeep(action);
    newAction.payload.tenantUserId = isFilteredByTenantUser ? newAction.payload.tenantUserId : "";

    const paginationInitialData = {
      limit: isCard ? 40 : 10,
      skip: 0,
      sortInfo: {},
      groupBy: [],
      filterValue: []
    };

    let paginationDataForView = selectedView?.data
      ? { ...paginationInitialData, ...selectedView.data }
      : paginationInitialData;

    if (syncPaginationToUrl) {
      syncPaginationToUrl({ ...paginationDataForView, uiViewTypeId: id });
    }

    newAction.payload.paginationData = { ...paginationDataForView, uiViewTypeId: id };

    dispatch(callEditUiSettingsUser(tenantUserId, newUiData, true));
    dispatch(newAction);
  };

  useEffect(() => {
    if (user?.[`${moduleValue}_list_view_id`]) {
      return selectView(user?.[`${moduleValue}_list_view_id`]);
    }

    const currentView = isFilteredByTenantUser
      ? "my_view"
      : isEmpty(selectedView)
        ? "default_view"
        : selectedView?.table_view_id || "default_view";
    selectView(currentView);
  }, []);

  // Derive whether a custom (non-default, non-my) view is currently selected.
  // Use this instead of selectedView?.table_view_id to avoid race conditions
  // where selectedView may not be fully populated yet.
  const isCustomViewSelected = selectedOriginalView && selectedOriginalView !== "default_view" && selectedOriginalView !== "my_view";

  // When list or selectedOriginalView changes, ensure selectedView is populated
  // from the Redux list so that view metadata (name, table_view_id, etc.) is available.
  useEffect(() => {
    if (list.length > 0 && isCustomViewSelected) {
      const view = list.find((v) => String(v.table_view_id) === String(selectedOriginalView));
      if (view && !selectedView?.table_view_id) {
        setSelectedView(view);
      }
    }
  }, [list, selectedOriginalView]);

  const handleSaveView = () => {
    if (isCustomViewSelected) {
      const viewFromList = list.find((v) => String(v.table_view_id) === String(selectedOriginalView));
      const currentView = viewFromList || selectedView;
      // selectedView.data is the live, user-edited view from StackTable (columns,
      // columnOrder, groupBy, widths, filteringStatus). Prefer it for custom views;
      // fall back to liveTableDraft (also covers edge timing). Query fields stay from pagination.
      const editedData = selectedView?.data || liveTableDraft || {};
      dispatch(
        callEditCustomTableViewpromise({
          moduleValue,
          viewId: selectedOriginalView,
          viewData: {
            ...currentView,
            data: {
              ...currentView.data,
              ...paginationData,
              ...editedData,
              filterValue: paginationData?.filterValue ?? editedData.filterValue,
              sortInfo: paginationData?.sortInfo ?? editedData.sortInfo,
              skip: paginationData?.skip,
              limit: paginationData?.limit,
              uiViewTypeId: paginationData?.uiViewTypeId
            }
          },
          withoutLoading: true
        })
      ).then((data) => {
        if (data) {
          const savedView = data?.table_view;
          if (savedView) {
            const parsedData =
              typeof savedView.data === "string" ? JSON.parse(savedView.data) : savedView.data;
            setSelectedView({
              ...savedView,
              data: parsedData
            });
            setLiveTableDraft(parsedData || null);
          }
          enqueueSnackbar("View saved.", { variant: "success" });
        }
      });
    }
  };

  const TableViewHeader = (
    <>
      <Box display="flex" alignItems="center">
        <TextField
          id="table-view"
          select
          size="small"
          variant="outlined"
          onChange={(e) => selectView(e.target.value)}
          value={selectedOriginalView}
          style={{
            width: !isMobile ? "30%" : "100%",
            minWidth: "100px"
          }}>
          <MenuItem value="default_view">{defaultViewText}</MenuItem>
          <MenuItem value="my_view" disabled={disableMyView}>
            {myItemsText}
          </MenuItem>
          {list.length > 0 && <Divider />}
          {list &&
            list.map((option) => (
              <MenuItem key={option.table_view_id} value={option.table_view_id}>
                {option.name}
              </MenuItem>
            ))}
        </TextField>

        {!isMobile && !isCardView && permissions.Add && (
          <HeaderActionButton title="Create view" onClick={() => setAddView(true)} size="small" />
        )}

        {!isMobile && !isCardView && permissions.Edit && (
          <>
            <HeaderActionButton
              title="Edit view"
              onClick={() => {
                const viewFromList = list.find((v) => String(v.table_view_id) === String(selectedOriginalView));
                setViewToEdit(viewFromList || selectedView);
              }}
              disabled={!isCustomViewSelected}
              size="small"
            />
            <HeaderActionButton
              title="Save view"
              onClick={handleSaveView}
              disabled={!isCustomViewSelected}
              size="small"
            />
          </>
        )}

        {!isMobile && !isCardView && permissions.Delete && (
          <HeaderActionButton
            title="Delete view"
            onClick={() => setViewToDeleteId(selectedOriginalView)}
            disabled={!isCustomViewSelected}
            size="small"
          />
        )}

        {!isMobile && (
          <Box display="flex" alignItems="center" style={{ padding: "0px 10px" }}>
            <TextField
              select
              size="small"
              variant="outlined"
              label="View"
              value={viewTypeId || 0}
              onChange={(e) => onSelectListViewType(e.target.value)}
              style={{ minWidth: "130px", width: "260px", margin: 0 }}>
              {listViewTypeOptions &&
                listViewTypeOptions.map((option) => (
                  <MenuItem key={option.code} value={option.code}>
                    {option.name}
                  </MenuItem>
                ))}
            </TextField>
          </Box>
        )}

        {!isMobile &&
          (moduleValue === "company" || moduleValue === "contact" || moduleValue === "product_service") &&
          (permissions.Import || permissions.Export) && (
            <>
              <Divider sx={{ margin: "0 10px" }} orientation="vertical" flexItem />

              <HeaderActionButton
                title={`Import ${importLabels[moduleValue]}`}
                onClick={() => setOpenImportDialog(true)}
                size="small"
                disabled={!permissions.Import}
              />
              <Import
                moduleName={moduleValue}
                handleClose={() => setOpenImportDialog(false)}
                isOpen={openImportDialog}
              />

              <HeaderActionButton
                title={`Export ${moduleValue}`}
                onClick={() => setOpenExportDialog(true)}
                size="small"
                disabled={permissions.Export && !(selected.length > 0)}
              />
              <Export
                isOpen={openExportDialog}
                closePopup={() => setOpenExportDialog(false)}
                selectedIds={selected}
                moduleValue={moduleValue}
                columns={visibleColumnNames}
              />
            </>
          )}
      </Box>

      {permissions.Add && (
        <TableViewDialog
          moduleValue={moduleValue}
          isNew
          isOpen={addView}
          viewData={{
            ...selectedView,
            // Prefer live StackTable draft so Create View keeps current widths/order/visibility
            // even when on All/My (where selectedView.data is intentionally not updated).
            data: { columns: defaultColumns, ...paginationData, ...(liveTableDraft || selectedView?.data || {}) }
          }}
          closePopup={() => setAddView(false)}
          onSuccess={(newView) => {
            if (newView?.table_view_id) {
              const parsedView = {
                ...newView,
                data: typeof newView.data === "string" ? JSON.parse(newView.data) : newView.data
              };
              selectView(parsedView.table_view_id, parsedView, true);
            }
          }}
        />
      )}

      {permissions.Edit && (
        <TableViewDialog
          moduleValue={moduleValue}
          viewData={viewToEdit}
          isOpen={viewToEdit !== undefined}
          closePopup={() => setViewToEdit(undefined)}
        />
      )}

      {permissions.Delete && viewToDeleteId && (
        <ActionDialog
          title="Delete selected View?"
          description="Do you want to permanently delete selected View?"
          successSnackMessage="View deleted."
          actionFunction={() => dispatch(callDeleteCustomTableView(moduleValue, viewToDeleteId))}
          onSuccess={() => selectView("default_view")}
          isOpen={viewToDeleteId !== undefined}
          isLoading={isLoadingView}
          closePopup={() => setViewToDeleteId(undefined)}
          errorId={CUSTOM_TABLE_VIEW_DELETE_CALL}
        />
      )}
    </>
  );

  return [selectedView, setTableViewDraft, TableViewHeader, isFiltering, isGrouping, setIsFiltering, selectedOriginalView];
}

useTableViewHeader.propTypes = {
  moduleValue: PropTypes.string.isRequired,
  defaultViewText: PropTypes.string,
  permissions: PropTypes.shape({
    Add: PropTypes.bool,
    Edit: PropTypes.bool,
    Delete: PropTypes.bool
  }).isRequired,
  tableRef: PropTypes.shape({
    current: PropTypes.shape({}).isRequired
  }).isRequired
};

useTableViewHeader.defaultProps = {
  defaultViewText: "All"
};

export default useTableViewHeader;
